import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Constrói a sequência de números de página a exibir, com elipses quando necessário.
 * Sempre mostra: 1, página corrente ± 1, última. Elipses preenchem gaps > 1.
 */
function buildPageSequence(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const seq: Array<number | 'ellipsis'> = [];
  const around = new Set<number>([1, total, current - 1, current, current + 1]);
  if (current <= 3) {
    around.add(2).add(3).add(4);
  }
  if (current >= total - 2) {
    around
      .add(total - 1)
      .add(total - 2)
      .add(total - 3);
  }
  const sorted = Array.from(around)
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]!;
    seq.push(cur);
    const next = sorted[i + 1];
    if (next !== undefined && next - cur > 1) seq.push('ellipsis');
  }
  return seq;
}

export function Pagination({ page, totalPages, onPageChange, className }: Props) {
  if (totalPages <= 1) return null;
  const sequence = buildPageSequence(page, totalPages);

  return (
    <nav
      aria-label="Paginação"
      className={cn('flex flex-wrap items-center justify-center gap-1', className)}
    >
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Página anterior"
      >
        <ChevronLeft size={16} />
      </Button>

      {sequence.map((entry, idx) =>
        entry === 'ellipsis' ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-sm text-muted-foreground" aria-hidden>
            …
          </span>
        ) : (
          <Button
            key={entry}
            variant={entry === page ? 'default' : 'outline'}
            size="icon"
            className={cn('h-9 w-9 font-medium', entry === page && 'pointer-events-none')}
            onClick={() => onPageChange(entry)}
            aria-current={entry === page ? 'page' : undefined}
            aria-label={`Página ${entry}`}
          >
            {entry}
          </Button>
        ),
      )}

      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Próxima página"
      >
        <ChevronRight size={16} />
      </Button>
    </nav>
  );
}
